import React from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { ServiceExtraActionLook, ServiceExtraChip, ServiceExtraChipTone, ServiceExtraItem } from './serviceExtraTypes';
import { ServiceExtrasSwitch } from './ServiceExtrasSwitch';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for ServicesPanel; each part uses its own slice of it */
import panelStyles from '../ServicesPanel.module.scss';
import styles from './ServiceExtras.module.scss';
/* eslint-enable css-modules/no-unused-class */

const CHIP_CLASS: Record<ServiceExtraChipTone, string | undefined> = {
  ok: panelStyles.chipOk,
  work: panelStyles.chipWork,
  bad: panelStyles.chipBad,
  muted: panelStyles.chipMuted,
  review: styles.chipReview,
  outline: styles.chipOutline,
};

const BUTTON_CLASS: Record<Exclude<ServiceExtraActionLook, 'link'>, string> = {
  primary: 'btn btn-sm btn-primary',
  button: 'btn btn-sm btn-outline-secondary',
};

/** The name and its chips: used open and folded. */
export const ServiceExtraName: React.FC<{ title: string; chips: ServiceExtraChip[] }> = ({ title, chips }) => (
  <span className={styles.name}>
    {title}
    {chips.map((chip) => (
      <span key={chip.id} className={clsx(panelStyles.chip, CHIP_CLASS[chip.tone])}>
        {withBrandWord(chip.text)}
      </span>
    ))}
  </span>
);

/**
 * One item of the open extras box (design spotify-canvas d3 "pl-exr", "pl-exsw"): the picture, the
 * name with the chips, the state sentence and the buttons; then the auto upload switch in place.
 */
const ServiceExtraItemViewComponent: React.FC<{ item: ServiceExtraItem }> = ({ item }) => {
  const auto = item.autoUpload ?? null;
  return (
    <div className={styles.item} data-testid={`service-extra-${item.id}`}>
      <div className={clsx(styles.itemLine, item.thumb === undefined && styles.itemLineNoThumb)}>
        {item.thumb !== undefined && <span className={styles.thumb}>{item.thumb}</span>}
        <div className={styles.text}>
          <ServiceExtraName title={item.title} chips={item.chips} />
          <small className={clsx(styles.status, item.statusIsBad === true && styles.statusBad)}>
            {withBrandWord(item.status)}
          </small>
          {withBrandWord(item.details)}
        </div>
        {(item.actions.length > 0 || (item.actionError ?? null) !== null) && (
          <div className={styles.actions}>
            {item.actions.map((action) =>
              action.href === undefined ? (
                <button
                  key={action.id}
                  type="button"
                  disabled={action.disabled}
                  className={action.look === 'link' ? panelStyles.link : BUTTON_CLASS[action.look]}
                  onClick={action.onClick}
                >
                  {action.label}
                </button>
              ) : (
                <a
                  key={action.id}
                  className={panelStyles.link}
                  href={action.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {action.label}
                </a>
              )
            )}
            {(item.actionError ?? null) !== null && (
              <span className={styles.actionError} role="alert">
                {withBrandWord(item.actionError)}
              </span>
            )}
          </div>
        )}
      </div>
      {auto !== null && (
        <div className={styles.auto} data-testid={`service-extra-auto-${item.id}`}>
          <div className={styles.autoLine}>
            <div className={styles.text}>
              <span className={styles.autoTitle}>{auto.title}</span>
              <small className={styles.autoSub}>{auto.sub}</small>
            </div>
            <ServiceExtrasSwitch
              label={auto.switchLabel}
              isOn={auto.isOn}
              isSaving={auto.isSaving}
              isDimmed={auto.isDimmed}
              onToggle={auto.onToggle}
              testId={`service-extra-switch-${item.id}`}
            />
          </div>
          {(auto.note ?? null) !== null && <span className={styles.note}>{auto.note}</span>}
          {(auto.error ?? null) !== null && (
            <span className={styles.actionError} role="alert">
              {auto.error}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const ServiceExtraItemView = TypedMemo(ServiceExtraItemViewComponent);
